import React from 'react';
import { motion } from 'framer-motion';
import { FaFileAlt, FaUserGraduate, FaAward } from 'react-icons/fa';

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: 0.8 } }
};

const slideIn = {
  hidden: { opacity: 0, x: -50 },
  show: { opacity: 1, x: 0, transition: { duration: 0.6 } }
};

const OnlineVerification = () => {
  const verificationTypes = [
    {
      id: 1,
      title: 'Personalized Support',
      description: 'Benefit from dedicated academic advisors and small class sizes that ensure individual attention and success.',
      icon: <FaUserGraduate className="w-8 h-8 text-primary" />
    },
    {
      id: 2,
      title: 'Flexible Learning',
      description: 'Access our programs through various study modes designed to fit your schedule and learning style.',
      icon: <FaAward className="w-8 h-8 text-primary" />
    },
    {
      id: 3,
      title: 'Global Recognition',
      description: 'Earn qualifications that are recognized and respected by leading universities and employers worldwide.',
      icon: <FaFileAlt className="w-8 h-8 text-primary" />
    }
  ];

  const verificationSteps = [
    {
      step: 1,
      title: 'Global Education Legacy',
      description: 'We have been providing the highest quality education to international students preparing to enter the world\'s leading universities for the last 31 years.'
    },
    {
      step: 2,
      title: 'International Excellence',
      description: 'Our commitment to innovation, academic excellence, and personalized support ensures every student is fully prepared for the next step in their academic journey.'
    },
    {
      step: 3,
      title: 'Pathway to Success',
      description: 'Our foundation and pathway programmes offer outstanding teaching and support for students from around the world.'
    }
  ];

  return (
    <motion.div 
      className="min-h-screen bg-gray-50"
      initial="hidden"
      animate="show"
      variants={container}
    >
      {/* Hero Section */}
      <motion.div 
        className="bg-gradient-to-r from-primary to-primary-dark py-20 text-white"
        variants={fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">Online Verification Portal</h1>
          <p className="text-xl max-w-3xl mx-auto text-white">
            Verify your academic credentials and documents with our secure online verification system.
          </p>
        </div>
      </motion.div>

      {/* About Section */}
      <motion.div 
        className="py-16 bg-white"
        variants={fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">31 Years of Academic Excellence</h2>
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-gray-700">
              <p>
                We have been providing the highest quality education to international students preparing to enter the world's leading universities for the last 31 years.
              </p>
              <p>
                Through our international Colleges and foundation and pathway programmes, we are focused on providing outstanding teaching and support for students from around the world.
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <motion.div 
        className="py-12"
        variants={fadeIn}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Discover the FIC Advantage</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experience world-class education with personalized support and global recognition
            </p>
          </div>

          {/* Verification Types */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {verificationTypes.map((type, index) => (
              <motion.div 
                key={type.id} 
                className="bg-white rounded-lg shadow-md p-8 hover:shadow-lg transition-shadow duration-300"
                variants={item}
                whileHover={{ scale: 1.03 }}
              >
                <motion.div 
                  className="flex justify-center mb-4"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  {type.icon}
                </motion.div>
                <h3 className="text-xl font-semibold text-center mb-3">{type.title}</h3>
                <p className="text-gray-600 text-center">{type.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Verification Info */}
          <motion.div 
            className="bg-white rounded-xl shadow-lg overflow-hidden mb-16 p-8"
            variants={slideIn}
            whileHover={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
          >
            <h2 className="text-2xl font-bold text-gray-900 mb-6">About Our Verification Services</h2>
            <p className="text-gray-600 mb-6 text-lg">
              Ensuring the authenticity of academic records is essential in today's global educational landscape. At FIC College, our verification services provide reliable, transparent, and efficient confirmation of qualifications for students, employers, and institutions.
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 mt-8 mb-4">Our service offers:</h3>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Trusted Academic Validation</h4>
                  <p className="text-gray-600">Verify student credentials with confidence through our officially maintained digital records.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Instant Access, Anytime</h4>
                  <p className="text-gray-600">Our online system allows verifications 24/7 from anywhere in the world.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Compliance with Global Standards</h4>
                  <p className="text-gray-600">We adhere to international data protection and verification protocols to guarantee integrity and privacy.</p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="text-green-500 mr-3 text-xl">✓</span>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Streamlined Experience</h4>
                  <p className="text-gray-600">With user-friendly tools and responsive support, verification is fast, simple, and secure.</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* The FIC Learning Experience */}
          <motion.div 
            className="bg-white rounded-xl shadow-lg overflow-hidden"
            variants={fadeIn}
            whileInView={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 50 }}
            viewport={{ once: true }}
          >
            <div className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">The FIC Learning Experience</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {verificationSteps.map((step) => (
                  <motion.div 
                    key={step.step} 
                    className="text-center"
                    variants={item}
                    whileHover={{ scale: 1.05 }}
                  >
                    <motion.div 
                      className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary text-white text-lg font-bold mb-4"
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.5 }}
                    >
                      {step.step}
                    </motion.div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default OnlineVerification;
